import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select, Textarea } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 45G — Force Update. ON/OFF, title, required version, description,
 * update type (Google Play Store / APK URL) and the APK URL. When ON the app
 * shows a blocking dialog and the Worker refuses API calls from older
 * builds (HTTP 426). Backend: routes/admin/force-update.js.
 */
interface ForceUpdateConfig {
  enabled: boolean;
  title: string;
  version: string;
  description: string;
  updateType: "play_store" | "apk";
  updateUrl: string;
}

const EMPTY: ForceUpdateConfig = {
  enabled: false,
  title: "Update required",
  version: "",
  description: "",
  updateType: "play_store",
  updateUrl: "",
};

export function ForceUpdatePage() {
  const [cfg, setCfg] = useState<ForceUpdateConfig>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<ForceUpdateConfig>("/admin/force-update")
      .then((c) => setCfg({ ...EMPTY, ...c }))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load Force Update settings"))
      .finally(() => setIsLoading(false));
  }, []);

  async function save() {
    setIsSaving(true);
    try {
      const saved = await apiRequest<ForceUpdateConfig>("/admin/force-update", { method: "PATCH", body: cfg });
      setCfg({ ...EMPTY, ...saved });
      showToast(saved.enabled ? "Force Update is ON" : "Force Update saved (OFF)", "success");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AppShell title="Force Update">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        When ON, every app older than the version below is blocked by an unskippable update dialog, and the server
        refuses its API calls until it is updated. Version format: 1.2.3 (compared with the app&apos;s versionName).
      </p>
      <Card className="max-w-2xl p-5">
        {error && <p className="mb-3 text-xs text-danger">{error}</p>}
        <label className="mb-4 flex items-center gap-3 text-sm font-semibold">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={cfg.enabled}
            disabled={isLoading}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          />
          Force Update {cfg.enabled ? "ON" : "OFF"}
        </label>

        <div className="space-y-3">
          <Input label="Title" value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} />
          <Input
            label="New version number"
            placeholder="e.g. 1.4.0"
            value={cfg.version}
            onChange={(e) => setCfg({ ...cfg, version: e.target.value })}
          />
          <Textarea
            label="Description"
            rows={3}
            value={cfg.description}
            onChange={(e) => setCfg({ ...cfg, description: e.target.value })}
          />
          <Select
            label="Update type"
            value={cfg.updateType}
            onChange={(e) => setCfg({ ...cfg, updateType: e.target.value as "play_store" | "apk" })}
          >
            <option value="play_store">Google Play Store</option>
            <option value="apk">APK URL</option>
          </Select>
          {cfg.updateType === "apk" && (
            <Input
              label="APK URL (https)"
              placeholder="https://example.com/4fun-1.4.0.apk"
              value={cfg.updateUrl}
              onChange={(e) => setCfg({ ...cfg, updateUrl: e.target.value })}
            />
          )}
        </div>

        <div className="mt-5 rounded-xl border border-line bg-surface-muted p-4">
          <p className="text-xs font-semibold uppercase text-ink-faint">Preview</p>
          <p className="mt-2 text-center text-base font-bold">{cfg.title || "Update required"}</p>
          <p className="mt-1 text-center text-xs font-semibold text-emerald-700">New Version: {cfg.version || "X.X.X"}</p>
          <p className="mt-2 text-center text-sm text-ink-faint">{cfg.description}</p>
          <div className="mx-auto mt-3 w-40 rounded-full bg-emerald-300 py-2 text-center text-sm font-bold text-ink">UPDATE</div>
        </div>

        <div className="mt-5">
          <Button onClick={save} isLoading={isSaving} disabled={isLoading}>
            Save
          </Button>
        </div>
      </Card>
    </AppShell>
  );
}
